import { apiBank, apiExchange, apiMerchant, apiMisc, apiNotify, apiReport, apiUser } from './config';
import axios from 'axios';

/**
 * Reads a single currency entry from a per-date bucket returned by api-report.
 * Buckets are shaped: { "2026-05-21": { ARS: { amount, count }, BRL: {...} } }.
 * Returns null when the bucket has no entry for the requested currency.
 */
export const pickByCurrency = (bucket, currency) => {
  if (!bucket || !currency) return null;
  if (Array.isArray(bucket)) {
    return bucket.find((b) => b?.currency_iso === currency) ?? null;
  }
  const entry = bucket[currency];
  if (!entry) return null;
  // Laravel groupBy(['date', 'currency']) in api-report wraps each leaf in an
  // array; emptyBucket fallbacks come through as plain objects. Normalize.
  return Array.isArray(entry) ? entry[0] ?? null : entry;
};

//   ---------- USER -----------

export const signIn = async (data) => {
  const request = await apiUser.post('/user/auth/login', data).then((res) => res.data);
  return request;
};

export const getUsers = async (queries) => {
  const filter = queries ? `?${queries}` : '';
  return apiUser.get(`/user${filter}`);
};

export const getUsernames = async (queries) => {
  return apiUser.get(`/user/names?${queries}`);
};
export const getUsersByIds = async (queries) => {
  const filter = queries ? `?${queries}` : '';
  return apiUser.get(`/user/names${filter}`).then((res) => res.data);
};

export const exportUsers = async (queries) => {
  const filter = queries ? `?${queries}` : '';
  const res = await apiUser.get(`/user/export/csv${filter}`, { responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'Users.csv';
  return { data: res.data, filename };
};

export const getMerchantUsers = async (queries) => {
  const filter = queries ? `?${queries}` : '';
  return apiUser.get(`/user/merchants${filter}`);
};

export const getOwnMerchantUsers = async (queries, signal) => {
  const filter = queries ? `?${queries}` : '';
  return apiUser.get(`/user/merchants/own${filter}`, { signal });
};

export const getMerchantUserById = async (userId) => {
  return apiUser.get(`/user/merchants/${userId}`).then((res) => res.data);
};

export const updateMerchantUserById = async (userId, payload) => {
  return apiUser.patch(`/user/merchants/${userId}`, payload).then((res) => res.data);
};

export const exportMerchantUsers = async (queries) => {
  const filter = queries ? `?${queries}` : '';
  const res = await apiUser.get(`/user/merchants/export/csv${filter}`, { responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'MerchantUsers.csv';
  return { data: res.data, filename };
};

export const getUserData = async (id) => apiUser.get(`/user/${id}`).then((res) => res);

export const editUserData = async (id, data) => apiUser.patch(`/user/${id}`, data).then((res) => res);

export const createUser = async (payload) => apiUser.post('/user', payload).then((res) => res);

export const getBankInfo = async (params, signal) =>
  await apiBank.get('/banks/get-transaction-info', { params, signal }).then((res) => res.data);

export const createMerchantUser = async (payload) => apiUser.post('/user/merchants', payload).then((res) => res);

export const createOwnMerchantUser = async (merchantSlug, payload) =>
  apiUser.post('/user/merchants/own', { ...payload, merchant_slug: merchantSlug }).then((res) => res);

export const updateUserPassword = async (payload) =>
  apiUser.patch('/user/password/update', payload).then((res) => res.data);

export const requestRecoverPassword = async (payload) =>
  apiUser.post('/user/password/recover', payload).then((res) => res.data);

export const updatePasswordWithToken = async (payload) =>
  apiUser.patch('/user/password/recover', payload).then((res) => res.data);

export const assignMerchant = async (userId, payload) =>
  apiUser.put(`/user/merchants/${userId}/assign`, payload).then((res) => res);

export const getUserRoles = async (id, signal) => apiUser.get(`/user/${id}/roles`, { signal }).then((res) => res.data);

export const getAvailableRoles = async (signal) => apiUser.get(`/user/roles`, { signal }).then((res) => res);

export const updateUserRoles = async (id, payload) => apiUser.put(`/user/${id}/roles`, payload).then((res) => res);

export const generateTwoFaCode = async (payload, token) => {
  if (token) {
    apiUser.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
  return apiUser.post('/user/2fa/generate-qr', payload).then((res) => res.data);
};

export const enableTwoFaWithSpecialToken = async (payload) => {
  return apiUser.post('/user/2fa/enable', payload).then((res) => res.data);
};

export const changeTwoFaStatus = async (payload, token) => {
  if (token) {
    apiUser.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  return apiUser.post('/user/2fa/change-status', payload).then((res) => res.data);
};

export const removeTwoFa = async (payload) => apiUser.patch('/user/2fa/remove', payload).then((res) => res.data);

// ------------- MERCHANT / CUSTOMERS -------------

export const getCustomers = async (queries) => {
  const filter = queries ? `?${queries}` : '';
  return apiMerchant.get(`/merchant/customers${filter}`);
};

export const getCountriesIso = async (signal) =>
  apiMerchant.get('/merchant/countries', { signal }).then((res) => res.data);

export const exportCustomers = async (queries) => {
  const filter = queries ? `?${queries}` : '';
  const res = await apiMerchant.get(`/merchant/customers/export/csv${filter}`, { responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'Customers.csv';
  return { data: res.data, filename };
};

export const getCustomerData = async (id, signal) =>
  apiMerchant.get(`/merchant/customers/${id}`, { signal }).then((res) => res.data);

export const updateCustomerData = async (id, payload) =>
  apiMerchant.patch(`/merchant/customers/${id}`, payload).then((res) => res.data);

export const getCustomerExternalData = async (payload, signal) =>
  apiMerchant.get('/merchant/customers/external-data', { params: payload, signal }).then((res) => res.data);

export const listCustomerDocuments = async (params, signal) =>
  apiMerchant.get(`/merchant/customers/documents?${params}`, { signal }).then((res) => res);

export const exportCustomerDocumentList = async (params, signal) => {
  const res = await apiMerchant.get(`/merchant/customers/documents/export/csv?${params}`, {
    signal,
    responseType: 'blob',
  });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'CustomerDocuments.csv';
  return { data: res.data, filename };
};

export const getCustomerDocument = async (docId, signal) =>
  apiMerchant.get(`/merchant/customers/documents/${docId}`, { signal }).then((res) => res.data);

export const uploadCustomerDocument = async (customerId, payload) =>
  apiMerchant.post(`/merchant/customers/documents/${customerId}`, payload).then((res) => res.data);

export const uploadAmlList = async (formData) =>
  apiMerchant
    .post('/merchant/customers/kyc/aml', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => res.data);

export const approveDocumentLevelThree = async (payload) =>
  apiMerchant.put('/merchant/customers/limits/level-3/approve', payload).then((res) => res.data);

export const refuseDocumentLevelThree = async (payload) =>
  apiMerchant.put('/merchant/customers/limits/level-3/refuse', payload).then((res) => res.data);

export const removePepStatus = async (userId) =>
  apiMerchant.patch(`/merchant/customers/${userId}/remove-pep`).then((res) => res.data);

export const manualKycRequest = async (payload) =>
  apiMerchant.post('/merchant/customers/documents/kyc-manual/request', payload).then((res) => res.data);

// ------------- MERCHANTS -------------

export const createMerchant = async (data) => apiMerchant.post('/merchant', data).then((res) => res.data.data);

export const getMerchants = async (params, signal) => apiMerchant.get(`/merchant?${params}`, { signal });

export const exportMerchants = async (params, signal) => {
  const res = await apiMerchant.get(`/merchant/export/csv?${params}`, { signal, responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'Merchants.csv';
  return { data: res.data, filename };
};

export const listMerchantsDropdown = async (signal, params = {}) =>
  apiMerchant.get('/merchant/dropdown', { signal, params }).then((res) => res.data);

export const getMerchantIndustriesDropdown = async (signal) =>
  apiMerchant.get('/merchant/industries/dropdown', { signal }).then((res) => res.data);

export const getMerchantById = async (id, signal) => apiMerchant.get(`/merchant/${id}`, { signal }).then((res) => res);

export const deleteMerchantById = async (merchantId, signal) =>
  apiMerchant.delete(`/merchant/${merchantId}`, {}, { signal }).then((res) => res);

export const updateMerchant = async (id, payload, signal) =>
  apiMerchant.patch(`/merchant/${id}`, payload, { signal }).then((res) => res);

export const updateMerchantsListOrder = async (payload, signal) =>
  apiMerchant.patch('/merchant/list-order', payload, { signal }).then((res) => res.data);

export const getOAuthClient = async (signal) =>
  apiMerchant.get('/merchant/oauth/get', { signal }).then((res) => res.data);

export const generateSecretKey = async () =>
  apiMerchant.patch('/merchant/oauth/generate-secret').then((res) => res.data);

export const generateWebhookSecretKey = async () =>
  apiMerchant.patch('/merchant/oauth/generate-webhook-secret').then((res) => res.data);

// ------------- MERCHANTS / WALLETS-------------

export const getMerchantWallets = async (signal) =>
  apiMerchant.get('/merchant/wallets', { signal }).then((res) => res.data);

export const getWalletByMerchantId = async (id, signal) =>
  apiMerchant.get(`/merchant/wallets?merchant_id[]=${id}`, { signal }).then((res) => res);

export const setMerchantDefaultWallet = async (walletId) =>
  apiMerchant.patch(`/merchant/wallets/${walletId}/default`).then((res) => res);

export const createMerchantWallet = async (payload) =>
  apiMerchant.post('/merchant/wallets', payload).then((res) => res);

export const createMultipleMerchantWallets = async (payload) =>
  apiMerchant.post('/merchant/wallets/many', payload).then((res) => res.data);

export const updateMerchantWallet = async (walletId, payload) =>
  apiMerchant.patch(`/merchant/wallets/${walletId}`, payload).then((res) => res);

export const deleteMerchantWallet = async (walletId) =>
  apiMerchant.delete(`/merchant/wallets/${walletId}`).then((res) => res);

export const getSettlementWallets = async (merchantId, signal) =>
  apiMerchant
    .get('/merchant/settlement/wallets', { params: merchantId ? { merchant_id: merchantId } : {}, signal })
    .then((res) => res);

export const createSettlementWallet = async (payload) =>
  apiMerchant.post('/merchant/settlement/wallets', payload).then((res) => res);

export const updateSettlementWallet = async (id, payload) =>
  apiMerchant.patch(`/merchant/settlement/wallets/${id}`, payload).then((res) => res);

export const deleteSettlementWallet = async (id) =>
  apiMerchant.delete(`/merchant/settlement/wallets/${id}`).then((res) => res);

export const getOwnSettlementWallets = async (merchantId, signal) =>
  apiMerchant.get(`/merchant/settlement/wallets/own?merchant_id=${merchantId}`, { signal }).then((res) => res);

export const createOwnSettlementWallet = async (payload) =>
  apiMerchant.post('/merchant/settlement/wallets/own', payload).then((res) => res);

export const approveSettlementWallet = async (id) =>
  apiMerchant.patch(`/merchant/settlement/wallets/${id}/approve`).then((res) => res);

export const refuseSettlementWallet = async (id) =>
  apiMerchant.patch(`/merchant/settlement/wallets/${id}/refuse`).then((res) => res);

export const getSettlementEmails = async (merchantId, signal) =>
  apiMerchant.get(`/merchant/settlement/emails?merchant_id=${merchantId}`, { signal }).then((res) => res);

export const createSettlementEmail = async (payload) =>
  apiMerchant.post('/merchant/settlement/emails', payload).then((res) => res);

export const deleteSettlementEmail = async (merchantId, email) =>
  apiMerchant.delete('/merchant/settlement/emails', { data: { merchant_id: merchantId, email } }).then((res) => res);

export const updateKycValidationStatus = async (userId, payload) =>
  apiMerchant.patch(`/merchant/customers/${userId}/kyc/document-verify`, payload).then((res) => res.data);

// ------------- TRANSACTIONS -------------

export const getTransactions = async (params, signal) =>
  apiMerchant.get(`/merchant/transactions?${params}`, { signal }).then((res) => {
    return res;
  });

export const getExternalTransactions = async (params, signal) =>
  apiBank.get(`/banks/external-transactions?${params}`, { signal });

export const getTransactionErrors = async (params, signal) =>
  apiBank.get(`/banks/transactions/errors?${params}`, { signal }).then((res) => res.data);

export const exportTransactionErrors = async (params, signal) => {
  const res = await apiBank.get(`/banks/transactions/errors/export/csv?${params}`, { signal, responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'TransactionErrors.csv';
  return { data: res.data, filename };
};

export const multipleConciliateExternalTransactions = async (params, signal) =>
  apiBank
    .post('/banks/external-transactions/multiple-conciliation', params, {
      signal,
    })
    .then((res) => {
      return res;
    });

export const conciliateOrphanExternalTransactions = async (id, payload, signal) =>
  apiBank
    .patch(`/banks/external-transactions/${id}/orphan`, payload, {
      signal,
    })
    .then((res) => {
      return res;
    });

export const refundExternalTransactions = async (id, params, signal) =>
  apiBank
    .post(`/banks/external-transactions/${id}/refund`, params, {
      signal,
    })
    .then((res) => {
      return res;
    });

export const periodConciliation = async (params, signal) =>
  apiBank
    .post('/banks/external-transactions/period-conciliation', params, {
      signal,
    })
    .then((res) => {
      return res;
    });

export const getIssues = async (params, signal) =>
  apiMerchant.get(`/merchant/transactions/issues?${params}`, { signal }).then((res) => {
    return res;
  });

export const getIssuesCount = (signal) =>
  apiMerchant.get('/merchant/transactions/issues/open', { signal }).then((res) => res.data.data.open);

export const createIssue = async (payload) => {
  return apiMerchant.post('/merchant/transactions/issues', payload).then((res) => res.data);
};

export const deleteIssue = async (id) => {
  return apiMerchant.delete(`/merchant/transactions/issues/${id}`).then((res) => res.data);
};

export const updateIssue = async (id, payload) => {
  return apiMerchant.patch(`/merchant/transactions/issues/${id}`, payload).then((res) => res.data);
};

export const getIssueById = async (id) => {
  return apiMerchant.get(`/merchant/transactions/issues/${id}`).then((res) => res.data);
};

export const refundFailed = async (id) => {
  return apiMerchant.patch(`/merchant/transactions/${id}/refund-failed`).then((res) => res.data);
};

export const exportTransactions = async (params, signal) => {
  const res = await apiMerchant.get(`/merchant/transactions/export/csv?${params}`, { signal, responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'Transactions.csv';
  return { data: res.data, filename };
};

export const exportExternalTransactions = async (params) =>
  apiBank.post('/banks/external-transactions/export', params).then((res) => {
    return res;
  });

export const exportTransactionOffline = async (params) =>
  apiMerchant.get(`/merchant/transactions/export-offline?${params}`).then((res) => {
    return res.data;
  });

export const listTransactionCancelReasons = async (signal) =>
  apiMerchant.get('/merchant/transactions/cancel-reasons', { signal }).then((res) => res.data);

export const getTransactionById = async (transactionId, params) =>
  apiMerchant.get(`/merchant/transactions/${transactionId}`, {
    params,
  });

export const approveTransactionById = async (transactionId) =>
  apiMerchant.post(`/merchant/transactions/${transactionId}/approve`).then((res) => res.data);

export const refuseTransactionById = async (transactionId, payload) =>
  apiMerchant.post(`/merchant/transactions/${transactionId}/refuse`, payload).then((res) => res.data);

export const getAmountTransactions = async (params, signal) =>
  apiReport.get(`/report/transactions/amount?${params}`, { signal }).then((res) => res.data);

export const getDashboardCurrenciesDropdown = async (signal) =>
  apiReport.get('/report/currencies', { signal }).then((res) => res.data);

export const getAmountFeeProfits = async (params, signal) => {
  let url = '/report/transactions/amount?type=both&merchant_id=0';
  const paramsString = params ? params.toString() : '';
  if (!paramsString.includes('sum_columns')) {
    url += '&sum_columns[]=sum_fee_profit&sum_columns[]=sum_exchange_profit';
  }
  return apiReport.get(`${url}&${paramsString}`, { signal }).then((res) => res.data);
};

export const getAmountFeeSettlements = async (params, signal) =>
  apiReport
    .get(`/report/daily-reports/adjustment-fee?merchant_id=0&${params.toString()}`, { signal })
    .then((res) => res.data);

export const recalculateFee = async (id, payload) => apiMisc.post(`/misc/merchant/${id}/fees-updates`, payload);

export const getPendingTransactions = async (params) =>
  apiMerchant.get(`/merchant/transactions/reviews?${params}`).then((res) => res);

export const exportPendingTransactions = async (params) => {
  const res = await apiMerchant.get(`/merchant/transactions/reviews/export/csv?${params}`, { responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'PendingTransactions.csv';
  return { data: res.data, filename };
};

export const getPendingReviewTransactionSummary = async (params, signal) =>
  apiMerchant
    .get(`/merchant/transactions/reviews/summary?${params}`, {
      signal,
    })
    .then((res) => res.data);

export const getPendingWithdrawalsSummary = async (signal) =>
  apiMerchant
    .get('/merchant/transactions/pending-withdrawals/summary', {
      signal,
    })
    .then((res) => res.data);

export const getApprovedWithdrawalsSummary = async (signal) =>
  apiMerchant
    .get('/merchant/transactions/approved-withdrawals/summary', {
      signal,
    })
    .then((res) => res.data);

export const manualUpdateTransaction = async (transactionId, action, payload) =>
  apiMerchant.patch(`/merchant/transactions/${transactionId}/${action}`, payload).then((res) => res.data);

export const updateDepositAmount = async (transactionId, payload) =>
  apiMerchant.patch(`/merchant/transactions/${transactionId}/update-amount`, payload).then((res) => res.data);

export const resendTransactionCallbackById = async (transactionId) =>
  apiMerchant.post('/merchant/transactions/callbacks/single', { id: transactionId }).then((res) => res.data);

export const previewCallbacks = async (formData) =>
  apiMerchant
    .post('/merchant/transactions/callbacks/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => res.data);

export const resendMultipleCallbacks = async (ids) =>
  apiMerchant.post('/merchant/transactions/callbacks/multiple', { ids }).then((res) => res.data);

export const getAdjustmentsTypes = async (signal) =>
  apiMerchant.get('/merchant/transactions/adjustment/types', { signal }).then((res) => res.data);

export const transactionsAdjustment = async (payload) => {
  return apiMerchant.post('/merchant/transactions/adjustment', payload).then((res) => res.data);
};

export const updateTransactionFlagged = async (txId, payload) =>
  apiMerchant.patch(`/merchant/transactions/${txId}/flag`, payload).then((res) => res.data);

export const getSettlementTransactions = async (params, signal) =>
  apiMerchant.get(`/merchant/settlement/transactions?${params}`, { signal }).then((res) => res.data);

export const getSettlementTransaction = async (id, signal) =>
  apiMerchant.get(`/merchant/settlement/transactions/${id}`, { signal }).then((res) => res.data);

export const createSettlementTransaction = async (payload) =>
  apiMerchant.post('/merchant/settlement/transactions', payload).then((res) => res.data);

export const processSettlementTransaction = async (id, payload) =>
  apiMerchant.patch(`/merchant/settlement/transactions/${id}/process`, payload).then((res) => res.data);

export const confirmSettlementTransaction = async (id) =>
  apiMerchant.patch(`/merchant/settlement/transactions/${id}/confirm`).then((res) => res.data);

export const completeSettlementTransaction = async (id, payload) =>
  apiMerchant.patch(`/merchant/settlement/transactions/${id}/complete`, payload).then((res) => res.data);

export const cancelSettlementTransaction = async (id) =>
  apiMerchant.patch(`/merchant/settlement/transactions/${id}/cancel`).then((res) => res.data);

export const getOAuthAccessToken = async (clientId, clientSecret) => {
  const tempApi = axios.create({
    baseURL: apiMerchant.defaults.baseURL,
  });

  // Crear FormData (NO JSON)
  const formData = new FormData();
  formData.append('grant_type', 'client_credentials');
  formData.append('client_id', clientId);
  formData.append('client_secret', clientSecret);
  formData.append('scope', '*');

  return tempApi
    .post('/merchant/oauth/token', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((res) => res.data.access_token);
};

// --- FUNCIONES ACTUALIZADAS ---
export const simulateDeposit = async (payload, customHeaders = {}) => {
  const tempApi = axios.create({
    baseURL: apiMerchant.defaults.baseURL,
  });

  return tempApi
    .post('/merchant/transactions/external/deposit', payload, {
      headers: customHeaders,
    })
    .then((res) => res.data);
};

export const simulateWithdrawal = async (payload, customHeaders = {}) => {
  const tempApi = axios.create({
    baseURL: apiMerchant.defaults.baseURL,
  });

  return tempApi
    .post('/merchant/transactions/external/withdrawal', payload, {
      headers: customHeaders,
    })
    .then((res) => res.data);
};

// ------------- REPORTS -------------

export const getSummaryTransactionsByDate = async (params, signal) =>
  apiReport
    .get('/report/transactions/daily/merchant/summary/by-date', {
      params,
      signal,
    })
    .then((res) => res);

export const updateSummaryTransactionsByDate = async (payload) =>
  apiReport.put('/report/transactions/daily/merchant', payload).then((res) => res);

export const getSummaryTransactionsByBank = async (params, signal) =>
  apiReport
    .get(`/report/transactions/daily/merchant-bank?${params}`, {
      signal,
    })
    .then((res) => res);

export const updateSummaryTransactionsByBank = async (payload) =>
  apiReport.put('/report/transactions/daily/merchant-bank', payload).then((res) => res);

export const exportSummaryTransactionsByDate = async (params, signal) => {
  const res = await apiReport.get(`/report/transactions/daily/merchant/summary/by-date/export/csv?${params}`, {
    signal,
    responseType: 'blob',
  });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'SummaryTransactionsByDate.csv';
  return { data: res.data, filename };
};

export const exportSummaryTransactionsByBank = async (params, signal) => {
  const res = await apiReport.get(`/report/transactions/daily/merchant-bank/summary/by-date/export/csv?${params}`, {
    signal,
    responseType: 'blob',
  });

  const disposition = res.headers['content-disposition'];
  const filename = disposition
    ? disposition.split('filename=')[1]?.replace(/"/g, '')
    : 'MerchantBankDailyTransactions.csv';

  return { data: res.data, filename };
};

export const getWalletBalanceHistory = (params, signal) =>
  apiMerchant.get(`/merchant/wallets/history/list?${params}`, { signal }).then((res) => res.data);

export const exportWalletBalanceHistory = async (params, signal) => {
  const res = await apiMerchant.get(`/merchant/wallets/history/export/csv?${params}`, { signal, responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'WalletBalanceHistory.csv';
  return { data: res.data, filename };
};

export const getSummaryTransactionsByCustomer = async (params, signal) =>
  apiReport
    .get(`/report/transactions/summary/by-customer?${params}`, {
      signal,
    })
    .then((res) => res);

export const exportSummaryTransactionsByCustomer = async (params, signal) => {
  const res = await apiReport.get(`/report/transactions/summary/by-customer/export/csv?${params}`, {
    signal,
    responseType: 'blob',
  });
  const disposition = res.headers['content-disposition'];
  const filename = disposition
    ? disposition.split('filename=')[1]?.replace(/"/g, '')
    : 'SummaryTransactionsByCustomer.csv';
  return { data: res.data, filename };
};

export const fetchGuestTokenFromBackend = async (token) => {
  return await apiReport
    .get(`/report/superset-token/${token}`)
    .then((res) => res.data.data.guest_token.token)
    .catch((error) => error);
};

export const getMonthlyReport = async (queries = '', signal) =>
  apiReport.get(`/report/monthly-reports?${queries}`, { signal }).then((res) => res.data);

export const getDailyReport = async (queries = '', signal) =>
  apiReport.get(`/report/daily-reports?${queries}`, { signal }).then((res) => res.data);

export const downloadMonthlyReport = async () => apiReport.get('/report/monthly-reports/download').then((res) => res);

export const downloadMonthlyReportById = async (reportId) =>
  apiReport.get(`/report/monthly-reports/${reportId}/download`).then((res) => res.data);

export const downloadDailyReportById = async (reportId) =>
  apiReport.get(`/report/daily-reports/${reportId}/download`).then((res) => res.data);

export const getMonthlyReportByMerchantId = async (params, signal) => {
  const merchantId = params.get('merchant_id[]');
  params.delete('merchant_id[]');
  return apiReport
    .get(`/report/monthly-reports/${merchantId}?${params}`, {
      signal,
    })
    .then((res) => res.data);
};

export const getDailyReportByMerchantId = async (params, signal) => {
  const merchantId = params.get('merchant_id[]');
  params.delete('merchant_id[]');
  return apiReport.get(`/report/daily-reports/${merchantId}?${params}`, { signal }).then((res) => res.data);
};

export const adminExportMonthlyReportByMerchantId = async (params, signal) => {
  const merchantId = params.get('merchant_id[]');
  params.delete('merchant_id[]');
  const res = await apiReport.get(`/report/monthly-reports/${merchantId}/export/csv`, { signal, responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'MonthlyReport.csv';
  return { data: res.data, filename };
};

export const adminExportDailyReportByMerchantId = async (params, signal) => {
  const merchantId = params.get('merchant_id[]');
  params.delete('merchant_id[]');
  const res = await apiReport.get(`/report/daily-reports/${merchantId}/export/csv`, { signal, responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'DailyReport.csv';
  return { data: res.data, filename };
};

export const merchantExportMonthlyReport = async (params, signal) => {
  const res = await apiReport.get(`/report/monthly-reports/export/csv`, { signal, responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'MonthlyReport.csv';
  return { data: res.data, filename };
};

export const merchantExportDailyReport = async (params, signal) => {
  const res = await apiReport.get(`/report/daily-reports/export/csv`, { signal, responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'DailyReport.csv';
  return { data: res.data, filename };
};

export const countTransactionByBank = async (params, signal) =>
  apiReport.get('/report/transactions/count', { params, signal }).then((res) => res.data);

export const listAudits = async (params, signal) =>
  apiReport.get('/report/audits', { params, signal }).then((res) => res.data);

export const listAuditEntities = async (signal) =>
  apiReport.get('/report/audits/entities', { signal }).then((res) => res.data);

export const listProviderRequests = async (params, signal) =>
  apiReport.get('/report/providers/requests', { params, signal }).then((res) => res.data);

export const listProviderRequestsMonthly = async (params, signal) =>
  apiReport.get('/report/providers/requests/monthly', { params, signal }).then((res) => res.data);

export const recomputeProviderRequestsMonthly = async (payload) =>
  apiReport.put('/report/providers/requests/monthly/recompute', payload).then((res) => res.data);

// ------------- BANKS -------------

export const getBankList = async (params, signal) => apiBank.get(`/banks?${params}`, { signal });

export const exportBankList = async (params, signal) => {
  const res = await apiBank.get(`/banks/export/csv?${params}`, { signal, responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'Banks.csv';
  return { data: res.data, filename };
};

export const getBank = async (bankId, signal) => apiBank.get(`/banks/${bankId}`, { signal }).then((res) => res);

export const createBank = async (payload) => apiBank.post('/banks', payload).then((res) => res.data);

export const updateBank = async (bankId, payload) => apiBank.put(`/banks/${bankId}`, payload).then((res) => res);

export const deleteBankById = async (bankId) => apiBank.delete(`/banks/${bankId}`).then((res) => res);

export const listPaymentMethodsByBankId = async (bankId, signal) =>
  apiBank.get(`/banks/bank-methods?bank_id[]=${bankId}`, { signal }).then((res) => res.data);

export const createBankMethod = async (payload) => apiBank.post('/banks/bank-methods', payload).then((res) => res);

export const updateBankMethod = async (methodId, payload) =>
  apiBank.put(`/banks/bank-methods/${methodId}`, payload).then((res) => res);

export const deleteBankMethod = async (methodId) =>
  apiBank.delete(`/banks/bank-methods/${methodId}`).then((res) => res.data);

export const getPaymentMethods = async (signal) =>
  apiBank.get('/banks/methods?active=1', { signal }).then((res) => res);

export const simulateBankCallbackTransaction = async (payload) =>
  apiMerchant.patch('/merchant/transactions/simulate/status', payload).then((res) => res.data);

export const getAccountHolder = async (params) => {
  return await apiBank.get(`/banks/payment-accounts?${params}`).then((res) => res.data);
};
export const listCustomerBanks = async (params, signal) =>
  apiBank.get(`/banks/customer-banks?${params}`, { signal }).then((res) => res.data);

export const createCustomerBanks = async (payload) =>
  apiBank.post('/banks/customer-banks', payload).then((res) => res.data);

export const updateCustomerBankById = async (id, payload) =>
  apiBank.patch(`/banks/customer-banks/${id}`, payload).then((res) => res.data);

export const deleteCustomerBankById = async (id) =>
  apiBank.delete(`/banks/customer-banks/${id}`).then((res) => res.data);

export const listBanksDropdown = async ({ signal, currencyIso } = {}) =>
  apiBank
    .get('/banks/dropdown', { signal, params: currencyIso ? { currency_iso: currencyIso } : {} })
    .then((res) => res.data);

export const getSummaryByMonth = async (params, signal) =>
  apiReport.get('/report/transactions/daily/merchant-bank/summary/by-month', {
    params,
    signal,
  });

export const getSummaryByBank = async (params, signal) =>
  apiReport.get('/report/transactions/daily/bank/summary/by-bank', {
    signal,
  });

export const updateBankOverdraft = async (bankId, payload) =>
  apiBank.patch(`/banks/${bankId}/overdraft`, payload).then((res) => res.data);

export const getCbusList = async (signal) =>
  apiBank.get('/banks/cbus?per_page=1000', { signal }).then((res) => res.data);

export const getCbuBalances = async (signal) => apiBank.get('/banks/cbus/balances', { signal }).then((res) => res.data);

export const listCbus = async (params, signal) =>
  apiBank.get(`/banks/cbus?${params}`, { signal }).then((res) => res.data);

export const createCbu = async (payload) => apiBank.post('/banks/cbus', payload).then((res) => res.data);

export const updateCbuById = async (id, payload) => apiBank.patch(`/banks/cbus/${id}`, payload).then((res) => res.data);

export const deleteCbuById = async (id) => apiBank.delete(`/banks/cbus/${id}`).then((res) => res.data);

// ------------- MONITOR -------------

export const setConversionMonitorConfig = async (payload) =>
  apiBank.patch('/banks/conversion-monitor/config', payload).then((res) => res.data);

export const getConversionMonitorConfig = async () =>
  apiBank.get('/banks/conversion-monitor/config').then((res) => res.data);

export const getConversionMonitorRates = async (params, signal) =>
  apiBank.get(`/banks/conversion-monitor/rates?${params}`, { signal });

export const getConversionMonitorSuspensions = async (params, signal) =>
  apiBank.get(`/banks/conversion-monitor/suspensions?${params}`, { signal });

export const getConversionMonitorEvents = async (params, signal) =>
  apiBank.get(`/banks/conversion-monitor/events?${params}`, { signal });

export const getConversionMonitorEventActions = async (signal) =>
  apiBank.get('/banks/conversion-monitor/events/actions', { signal }).then((res) => res.data);

export const deleteConversionMonitorSuspension = async (id) =>
  apiBank.delete(`/banks/conversion-monitor/suspensions/${id}`).then((res) => res.data);

// ------------- EXCHANGE -------------

export const getCurrencies = async (signal) => apiExchange.get('/exchange/currencies', { signal }).then((res) => res);

export const getExchangeConversion = async (payload, signal) =>
  apiExchange.post('/exchange/conversion', payload, { signal }).then((res) => res.data);

// ------------- NOTES -------------

export const getNoteByEntity = async (entity, entityId) =>
  apiMisc.get(`/misc/notes?entity_slug[]=${entity}&entity_id[]=${entityId}&per_page=50`).then((res) => res.data);

export const createNote = async (payload) => apiMisc.post('/misc/notes', payload).then((res) => res.data);

export const updateNoteById = async (id, payload) =>
  apiMisc.patch(`/misc/notes/${id}`, payload).then((res) => res.data);

export const deleteNoteById = async (id) => apiMisc.delete(`/misc/notes/${id}`).then((res) => res.data);

// ------------- NOTIFY -------------

export const listNotifications = async (params, signal) =>
  apiNotify.get(`/notify/notifications?${params}`, { signal }).then((res) => res);

export const exportNotificationList = async (params, signal) => {
  const res = await apiNotify.get(`/notify/notifications/export/csv?${params}`, { signal, responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'Notifications.csv';
  return { data: res.data, filename };
};

// CASHOUT

export const getCashoutList = async (params, signal) =>
  await apiBank.get(`/banks/cashouts?${params}`, { signal }).then((res) => res);

export const getCashoutReceipt = async (cashoutId, signal) =>
  await apiBank.get(`/banks/cashouts/${cashoutId}/receipt`, { signal }).then((res) => res);

export const createCashout = async (payload) => await apiBank.post('/banks/cashouts', payload).then((res) => res);

export const getCashoutById = async (cashoutId, signal) =>
  await apiBank.get(`/banks/cashouts/${cashoutId}`, { signal }).then((res) => res);

export const uploadCashoutReceipt = async (cashoutId, payload) =>
  await apiBank
    .post(`/banks/cashouts/${cashoutId}/receipt`, payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => res);

export const exportCashoutList = async (params, signal) => {
  const res = await apiBank.get(`/banks/cashouts/export/csv?${params}`, { signal, responseType: 'blob' });
  const disposition = res.headers['content-disposition'];
  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'Cashouts.csv';
  return { data: res.data, filename };
};

// ---------- PIX AND GO ----------

export const getPixAndGoQrList = async (params, signal) =>
  apiBank.get(`/banks/pix-and-go?${params}`, { signal }).then((res) => res.data);

export const simulatePixAndGoPayment = async (ulid) =>
  apiBank.post(`/banks/pix-and-go/${ulid}/simulate-payment`).then((res) => res.data);

export const getPixAndGoTransactions = async (params, signal) =>
  apiMerchant.get(`/merchant/pix-and-go?${params}`, { signal }).then((res) => res.data);
